import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UiModule } from 'src/app/ui/ui.module';
import { PipeModule } from 'src/app/pipes/pipes.module';
import { TooltipDirective } from '../../directives/pncl-tooltip.directive';
import { PencilButtonComponent } from '../pencil-button/pencil-button.component';
import { IconTypes, PencilIconComponent } from '../pencil-icon/pencil-icon.component';
import { PencilMultiSelectDropdownComponent } from '../pencil-multi-select-dropdown/pencil-multi-select-dropdown.component';
import { PencilPillComponent } from '../pencil-pill/pencil-pill.component';

export interface MultiSelectTagItem {
  label: string;

  [key: string]: unknown;
}

/**
 * Component for searching and selecting multiple tags.
 * @template T - The type of the items in the addableList and selectedList arrays.
 */
@Component({
  selector: 'app-pencil-multi-select-tags',
  templateUrl: './pencil-multi-select-tags.component.html',
  styleUrls: ['./pencil-multi-select-tags.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PencilPillComponent,
    PencilIconComponent,
    TranslateModule,
    PencilButtonComponent,
    CommonModule,
    FormsModule,
    TooltipDirective,
    UiModule,
    PencilMultiSelectDropdownComponent,
    PipeModule,
  ],
})
export class PencilMultiSelectTagsComponent<T extends { [key: string]: any }>
  implements OnInit, OnChanges
{
  @Input() addableList: T[] = [];
  @Input() selectedList: T[] = [];
  @Input() maxShownTags: number = 4;
  @Input() hasEditOption = false;
  @Input() placeholder = this.translate.instant('Add');
  @Input() keyPropName = 'key';
  @Input() valuePropName = 'value';
  @Input() header?: string;
  @Input() canEdit = false;
  @Input() canExpandHiddenTags = true;

  @Output() selectedItemsChange = new EventEmitter<T[]>();
  @Output() editOptionClicked = new EventEmitter<void>();

  @ViewChild(PencilMultiSelectDropdownComponent, { static: false })
  dropdownComponent!: PencilMultiSelectDropdownComponent<T>;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.originalAddableList = this.addableList;
    this.updateLists(new Set(this.selectedList));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Keep original addable list in sync when a linked filter changes them
    // e.g. selecting a site should limit the programs available for selection.
    if (changes.addableList && !changes.addableList.firstChange) {
      this.originalAddableList = changes.addableList.currentValue;
    }

    // This check updates the lists when either the addableList or selectedList changes.
    // Detecting selectedList changes is necessary because the addableList has to filter out the already selected items.
    // Detecting addableList changes is necessary because _originalAddableList may get updated as per if check above (mostly linked filters use case),
    // so the addableList needs to be promptly recomputed to avoid duplicate or stale options from being shown (from previous original addable list).
    if (
      (changes.addableList && !changes.addableList.firstChange) ||
      (changes.selectedList && !changes.selectedList.firstChange)
    ) {
      this.updateLists(new Set(this.selectedList));
    }
  }

  originalAddableList: T[] = this.addableList;

  searchBoxHidden = true;

  get displayedSelectedList(): T[] {
    this.maxShownTags = this.canEdit ? -1 : this.maxShownTags;
    return this.maxShownTags > -1
      ? this.selectedList.slice(0, this.maxShownTags)
      : this.selectedList;
  }

  get hiddenSelectedList(): T[] {
    return this.selectedList.slice(this.maxShownTags);
  }

  get moreTagsToShow(): boolean {
    return this.maxShownTags > -1 && this.selectedList.length > this.maxShownTags;
  }

  get buttonClasses(): { [key: string]: boolean } {
    const isSelectedListEmpty = this.selectedList.length === 0;

    return {
      'pencil-button': true,
      small: isSelectedListEmpty,
      white: isSelectedListEmpty,
      'placeholder-btn': isSelectedListEmpty,
      'add-btn': !isSelectedListEmpty,
    };
  }

  focusSearchInput(): void {
    this.searchBoxHidden = false;
  }

  private updateLists(selectedSet: Set<T>): void {
    this.selectedList = Array.from(selectedSet);
    this.addableList = this.originalAddableList.filter((i) => !selectedSet.has(i));
  }

  addSelectedItem(selectedItem: T): void {
    const selectedSet = new Set(this.selectedList);
    selectedSet.add(selectedItem);
    this.updateLists(selectedSet);
    this.selectedItemsChange.emit([...this.selectedList]);
  }

  removeSelectedItem(selectedItem: T): void {
    const selectedSet = new Set(this.selectedList);
    selectedSet.delete(selectedItem);
    this.updateLists(selectedSet);
    this.selectedItemsChange.emit([...this.selectedList]);
  }

  showFullList(): void {
    this.maxShownTags = this.addableList.length;
  }

  protected IconTypes = IconTypes;
}
